/** @module Swipe mode: a stack of cases, right confirms, left shows the next suggestion, up drops. */
import { Check, ChevronLeft, Crop, Search, X } from "lucide-react";
import { type PointerEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { CardFace } from "@/components/card-face";
import { CardPicker, startingQuery } from "@/components/card-picker";
import { OcrBadge } from "@/features/review/ocr-badge";
import { ResolveForm } from "@/components/resolve-form";
import type { ReviewModel } from "@/features/review/use-review-case";
import { printedNumber, useCard } from "@/lib/cards";

/** Pixels a card has to travel before letting go decides. */
export const COMMIT = 96;
/** Pixels before the stamp ("Bestätigen") shows what letting go would do. */
const HINT = 32;

export type Verdict = "confirm" | "other" | "dismiss" | null;

/** Which decision a drag stands for; sideways wins over up unless it is clearly upwards. */
export function verdictOf(dx: number, dy: number, threshold = HINT): Verdict {
  if (-dy > threshold && -dy > Math.abs(dx)) return "dismiss";
  if (dx > threshold) return "confirm";
  if (dx < -threshold) return "other";
  return null;
}

function reducedMotion(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

export function SwipeDeck({ model }: { model: ReviewModel }) {
  const { t, i18n } = useTranslation();
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const calm = reducedMotion();
  const card = useCard(model.chosen);
  const at = model.candidates.findIndex((candidate) => candidate.print_id === model.chosen);
  const candidate = model.candidates[at];
  const verdict = drag ? verdictOf(drag.dx, drag.dy) : null;

  const decide = (choice: Verdict) => {
    if (choice === "confirm") model.confirm();
    if (choice === "dismiss") model.discard();
    if (choice === "other") model.nextCandidate();
  };

  const start = (event: PointerEvent<HTMLDivElement>) => {
    if (model.busy || model.settled || event.button !== 0) return;
    // Buttons inside the card (chips) keep working; only the card itself is dragged.
    if ((event.target as HTMLElement).closest("button")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const originX = event.clientX;
    const originY = event.clientY;
    const target = event.currentTarget;
    const move = (step: globalThis.PointerEvent) => {
      // Functional update: every step builds on the newest position, never a stale one.
      setDrag(() => ({ dx: step.clientX - originX, dy: step.clientY - originY }));
    };
    const stop = (end: globalThis.PointerEvent) => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
      target.removeEventListener("pointercancel", stop);
      const choice = verdictOf(end.clientX - originX, end.clientY - originY, COMMIT);
      setDrag(null);
      if (end.type === "pointerup") decide(choice);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
    target.addEventListener("pointercancel", stop);
  };

  // Reduced motion: the card stays put and only the stamp says what letting go would do.
  const transform =
    drag && !calm
      ? `translate(${String(drag.dx)}px, ${String(Math.min(0, drag.dy))}px) rotate(${String(drag.dx / 18)}deg)`
      : undefined;
  const stamp =
    verdict === "confirm" ? (
      <span className="verdict verdict-ok">
        <Check />
        {t(model.confirmKey)}
      </span>
    ) : verdict === "other" ? (
      <span className="verdict verdict-other">
        <ChevronLeft />
        {t("review.swipe.other")}
      </span>
    ) : verdict === "dismiss" ? (
      <span className="verdict verdict-drop">
        <X />
        {t(model.discardKey)}
      </span>
    ) : null;

  const last = at >= model.candidates.length - 1;
  let hint = t("review.swipe.gestures");
  if (verdict === "confirm") hint = t("review.swipe.letGoConfirm");
  else if (verdict === "dismiss") hint = t("review.swipe.letGoDismiss");
  else if (verdict === "other")
    hint = last ? t("review.swipe.letGoSearch") : t("review.swipe.letGoOther");

  return (
    <div className="flex flex-col gap-3">
      {/* The fanned-out cards behind stick out; clipping keeps the page from scrolling sideways. */}
      <div className="-mx-4 overflow-x-clip px-4 pb-5">
        <div className="deck h-[min(58dvh,520px)]">
          <div className="deck-card deck-back-2" aria-hidden="true" />
          <div className="deck-card deck-back" aria-hidden="true" />
          <div
            className={`deck-card deck-top ${model.settled ? "opacity-0 transition-opacity duration-300" : ""}`}
            style={{ transform, transition: drag ? "none" : "transform 200ms ease-out" }}
            onPointerDown={start}
          >
            {stamp}
            <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3">
              <div className="flex flex-col gap-1">
                {model.photo ? (
                  <img
                    src={model.photo}
                    alt={t("review.yourPhoto")}
                    className="card-shape w-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <span className="card-shape block bg-(--surface-2)" />
                )}
                <span className="t-xs t-muted text-center">{t("review.rectified")}</span>
              </div>
              <div className="flex min-w-0 flex-col gap-[6px]">
                <span className="row">
                  <span className="status status-review">?</span>
                  <b className="t-sm">
                    {candidate?.picked
                      ? t("review.picked")
                      : t("review.proposal", {
                          current: at + 1,
                          total: model.candidates.length,
                        })}
                  </b>
                </span>
                <b className="truncate text-[19px]">{card.data?.name ?? model.chosen ?? "–"}</b>
                <span className="t-sm t-muted truncate">
                  {card.data?.set_name ? `${card.data.set_name} · ` : ""}
                  <span className="num">{card.data ? printedNumber(card.data) : ""}</span>
                </span>
                {candidate && !candidate.picked ? (
                  <span className="row flex-wrap gap-[6px]">
                    <span className={`badge ${at === 0 ? "badge-accent" : "badge-neutral"}`}>
                      {t("review.score")}{" "}
                      <span className="num">
                        {candidate.score.toLocaleString(i18n.resolvedLanguage ?? "de", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </span>
                    <OcrBadge model={model} candidate={candidate} />
                  </span>
                ) : null}
              </div>
            </div>
            <div className="relative min-h-0 grow">
              {model.chosen ? (
                <div className="pointer-events-none absolute inset-0 flex justify-center">
                  <span className="card-shape block h-full w-auto">
                    <CardFace printId={model.chosen} name={card.data?.name} quality="high" />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <p className="gesture" aria-hidden="true">
        {verdict === null ? (
          <>
            <span>← {t("review.swipe.other")}</span>
            <span>↑ {t(model.discardKey)}</span>
            <span>{t(model.confirmKey)} →</span>
          </>
        ) : (
          <span>{hint}</span>
        )}
      </p>
      {/* Every gesture has a button: swiping is a shortcut, never the only way. */}
      <div className="swipe-actions">
        <button
          type="button"
          className="act"
          aria-label={t("review.swipe.otherLabel")}
          disabled={model.busy || model.settled}
          onClick={model.nextCandidate}
        >
          <ChevronLeft />
          {t("review.swipe.other")}
        </button>
        <button
          type="button"
          className="act"
          aria-label={t("review.swipe.dismissLabel")}
          disabled={model.busy || model.settled}
          onClick={model.discard}
        >
          <X />
          {t(model.discardKey)}
        </button>
        <button
          type="button"
          className="act act-ok"
          aria-label={t("review.swipe.confirmLabel")}
          disabled={!model.canConfirm}
          onClick={model.confirm}
        >
          <Check />
          {t(model.confirmKey)}
        </button>
      </div>
      {model.searching ? (
        <CardPicker
          initialQuery={startingQuery(model.ocr)}
          onPick={(printId) => {
            model.setChosen(printId);
            model.setSearching(false);
          }}
        />
      ) : null}
      <ResolveForm
        variants={model.variants}
        choice={model.choice}
        locale={i18n.resolvedLanguage ?? "de"}
        suggestedLanguage={model.scan.result?.language_suggestion}
        onChange={model.setChoice}
      />
      <div className="row-between">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            model.setSearching(true);
          }}
        >
          <Search />
          {t("review.searchCard")}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={model.busy}
          hidden={model.approving}
          onClick={() => {
            model.setEditing(true);
          }}
        >
          <Crop />
          {t("review.setCorners")}
        </button>
      </div>
    </div>
  );
}
